/* eslint-disable no-console */
import React, { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types'
import {
  Button, Spacer, Icon, Text
} from 'components/atoms'
import RichTextEditor from 'components/organisms/RichTextEditor/RichTextEditor';
import {
  EditorState, convertToRaw, convertFromRaw
} from 'draft-js';
import markdownToDraft from 'services/markdownToDraft'
import draftToMarkdown from 'services/draftToMarkdown'
import { richTextEditorOptions } from 'types'
import { remarkableCarouselPlugin, remarkableVideoPlugin, remarkableImagePlugin } from 'containers/RichTextEditorMarkdown/remarkable'
import { useEditorContext, actions } from 'context/EditorContext';
import { useDebounce, useCopyToClipboard } from 'react-use'
import TextareaAutosize from 'react-autosize-textarea'
import styled, { css } from 'styled-components/macro'
import { rem } from 'polished'
import { filterMarkdown, fixRawDraft, generateEditorPath } from 'services/generators'
import { useRouterContext } from 'hooks';

const RichTextEditorMarkdown = (
  {
    initialMarkdown, onEditorStateChange, isRaw, options
  }
) => {
  const [markdown, setMarkdown] = useState(initialMarkdown)
  const [raw, setRaw] = useState(JSON.stringify(initialMarkdown))
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [{ richTextEditorState, richTextEditorStateTrigger }, dispatch] = useEditorContext();

  const {
    history,
    match: {
      params: { pageId, lang, subweb },
    },
  } = useRouterContext();

  const editorHomePath = useMemo(
    () => generateEditorPath({ lang, pageId, subweb }
    ), [lang, pageId, subweb])

  // update store editorState debounced for performance
  useDebounce(
    () => {
      dispatch({
        type: actions.RICH_TEXT_EDITOR_STATE_UPDATE,
        data: editorState,
      });
    },
    1000,
    [editorState]
  )

  // update state from store only when triggered
  // otherwise it was causing a lot of performance issues
  useEffect(() => {
    setEditorState(richTextEditorState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [richTextEditorStateTrigger])

  // convert markdown to draftjs EditorState
  useEffect(() => {
    const markdownFiltered = filterMarkdown(initialMarkdown)
    const rawDraftObj = markdownToDraft(markdownFiltered, {
      remarkablePlugins: [remarkableCarouselPlugin, remarkableVideoPlugin, remarkableImagePlugin],
      blockTypes: {
        carousel_open() {
          return {
            type: 'atomic',
            text: ' ',
          }
        },
        video_open() {
          return {
            type: 'atomic',
            text: ' ',
          }
        },
        image_open() {
          return {
            type: 'atomic',
            text: ' ',
          }
        },
      },
      blockEntities: {
        carousel_open(item) {
          return {
            type: 'CAROUSEL',
            mutability: 'IMMUTABLE',
            data: item.data
          }
        },
        video_open(item) {
          return {
            type: 'VIDEO',
            mutability: 'IMMUTABLE',
            data: item
          }
        },
        image_open(item) {
          return {
            type: 'IMAGE',
            mutability: 'IMMUTABLE',
            data: item
          }
        },
      }
    });

    const rawDraftObjFix = fixRawDraft(rawDraftObj)
    const newEditorState = EditorState.createWithContent(convertFromRaw(rawDraftObjFix))
    setEditorState(newEditorState)
  }, [initialMarkdown])

  // convert EditorState to markdown
  const handleChange = (newEditorState, escapeMarkdownCharacters = true) => {
    const contentState = newEditorState.getCurrentContent()
    const rawDraftObj = convertToRaw(contentState)
    const markdownFromEditorState = draftToMarkdown(rawDraftObj, {
      entityItems: {
        CAROUSEL: {
          open() {
            return ''
          },
          close(entity) {
            return `
<carousel>
  ${entity.data}
</carousel>`;
          }
        },
        VIDEO: {
          open() {
            return '';
          },
          close(entity) {
            return `<video url="${entity.data.url}" />`;
          }
        },
        /*         LINK: {
          open() {
            return '';
          },
          close(entity) {
            return `[${entity.data.title}](${entity.data.url})`;
            // return `<a href="${entity.data.url}">${entity.data.title}</a>`;
          }
        }, */
        IMAGE: {
          open() {
            return '';
          },
          close(entity) {
            const title = entity.data.title ? ` "${entity.data.title}"` : ''
            return `![${entity.data.alt}](${entity.data.src}${title})`;
          }
        }
      },
      escapeMarkdownCharacters
    });

    setEditorState(newEditorState)
    onEditorStateChange(markdownFromEditorState)
    setMarkdown(markdownFromEditorState)
  }

  const handleStateChangeOptionHtml = (newEditorState) => {
    handleChange(newEditorState, false);
    setTimeout(() => {
      dispatch({
        type: actions.SET_MODULE_EDITED,
        data: {
          key: null,
          name: null,
        },
      });
      history.push(editorHomePath);
    }, 1000);
  }

  const [, copyToClipboard] = useCopyToClipboard();
  const handleMarkdownChange = (newMarkdown) => {
    setMarkdown(newMarkdown)
    onEditorStateChange(newMarkdown)
    setRaw(JSON.stringify(newMarkdown))
  }

  const handleRawChange = (e) => {
    const newRaw = e.target.value
    const newMarkdown = JSON.parse(newRaw)
    setRaw(newRaw)
    setMarkdown(newMarkdown)
    onEditorStateChange(newMarkdown)
  }

  return isRaw ? (
    <TextAreaWrapper>
      <Spacer bottom="xs">
        <Text modifiers={['l']}>Markdown:</Text>
      </Spacer>
      <TextareaAutosize
        value={markdown}
        onChange={(e) => handleMarkdownChange(e.target.value)}
        async
      />
      <Spacer bottom="s" top="s" modifiers={['small', 'primary']}>
        <Text modifiers={['l']}>Formato JSON:</Text>
        <Text modifiers={['xs', 'light']}>Cole com as aspas</Text>
      </Spacer>
      <TextareaAutosize
        value={raw}
        onChange={handleRawChange}
        async
      />
      <Spacer top="xs">
        <Button onClick={() => copyToClipboard(raw)} modifiers={['small']}>
          <Spacer right="xs">
            <Icon icon="fal fa-copy" height={16} />
          </Spacer>
          Copiar
        </Button>
      </Spacer>
    </TextAreaWrapper>
  ) : (
    <RichTextEditor
      editorState={editorState}
      onEditorStateChange={handleChange}
      onStateChangeOptionHtml={handleStateChangeOptionHtml}
      options={options}
    />
  )
}

const TextAreaWrapper = styled.div(props => css`
  textarea {
    width: 100%;
    line-height: 1.6;
    text-transform: none;
    font-size: ${rem('15px')};
    font-weight: ${props.theme.weight.light};
    letter-spacing: ${props.theme.values.letterSpacing};
    font-family: ${props.theme.type.base};
    color: ${(props.isThemeReverse
    ? props.theme.components.text.colorReverse
    : props.theme.components.text.color)};
    font-weight: ${props.theme.weight.light};
    padding: 1rem;
  }
`)

RichTextEditorMarkdown.propTypes = {
  editorState: PropTypes.shape({}),
  initialMarkdown: PropTypes.string,
  onEditorStateChange: PropTypes.func,
  options: richTextEditorOptions,
  isRaw: PropTypes.bool,
}

RichTextEditorMarkdown.defaultProps = {
  editorState: EditorState.createEmpty(),
  initialMarkdown: '',
  onEditorStateChange: () => null,
  options: {},
  isRaw: false,
}

export default RichTextEditorMarkdown;
